import { useCallback, useMemo, useState } from 'react';
import { api, ErreurApi, type AjustementCogo, type DerouleCogo, type LigneCheminement,
         type MethodeCalcul, type MethodeLeve, type UniteAngle } from './api';
import { Icone } from './icones';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CHEMINEMENT — la parcelle telle que l'acte l'énonce (D.927)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un titre foncier ancien ne porte aucune coordonnée. Il porte des GISEMENTS
 * et des DISTANCES : « du point B, gisement 112,45 gon, 43,20 m ». Sans cet
 * écran, reprendre ce stock imposait de DESSINER une figure « qui ressemble »
 * — c'est-à-dire de fabriquer des limites que l'acte n'énonce pas.
 *
 * ⚠️⚠️ TROIS CHOSES QUE CET ÉCRAN REFUSE DE TAIRE.
 *
 *  1. L'UNITÉ. « 100 » vaut 90° en grades et 100° en degrés. Les deux sont
 *     plausibles, et l'écart est de dix degrés. L'unité est donc choisie en
 *     tête, jamais supposée.
 *
 *  2. QUE BOWDITCH FERME TOUJOURS. C'est son objet et c'est son danger : il
 *     répartit l'écart sur tous les côtés, donc la figure ferme même quand une
 *     distance est fausse de dix mètres. L'écran affiche la fermeture AVANT
 *     compensation, et le service renvoie lui-même l'avertissement.
 *
 *  3. QU'UN AJUSTEMENT SANS REDONDANCE N'AJUSTE RIEN. Degré de liberté nul :
 *     les résidus sont nuls par construction, σ₀ vaut zéro, et « précision
 *     parfaite » serait le pire des verts. Le service rend alors `null`, et
 *     l'écran écrit « non éprouvé » — jamais « conforme ».
 */

const UNITES: Array<{ cle: UniteAngle; libelle: string; tour: number }> = [
  { cle: 'grade', libelle: 'Grades (gon) — 400 au tour', tour: 400 },
  { cle: 'degre', libelle: 'Degrés — 360 au tour', tour: 360 },
];

const FUSEAUX = [
  { zone: 28, libelle: 'UTM 28N — Conakry, Boké, la côte' },
  { zone: 29, libelle: 'UTM 29N — Kankan, Nzérékoré, l’intérieur' },
];

const METHODES: Array<{ cle: MethodeLeve; libelle: string }> = [
  { cle: 'station_totale', libelle: 'Station totale' },
  { cle: 'gnss_rtk', libelle: 'GNSS RTK' },
  { cle: 'gnss_statique', libelle: 'GNSS statique' },
  { cle: 'photo_interpretation', libelle: 'Reprise d’un acte ancien' },
];

const LIGNE_VIDE: LigneCheminement = { de: '', vers: '', gisement: 0, distance: 0 };

export function Cheminement() {
  const [unite, setUnite] = useState<UniteAngle>('grade');
  const [zone, setZone] = useState(29);
  const [depart, setDepart] = useState({ nom: 'A', x: '500000', y: '1050000' });
  const [lignes, setLignes] = useState<LigneCheminement[]>([
    { ...LIGNE_VIDE, de: 'A', vers: 'B' },
    { ...LIGNE_VIDE, de: 'B', vers: 'C' },
    { ...LIGNE_VIDE, de: 'C', vers: 'D' },
    { ...LIGNE_VIDE, de: 'D', vers: 'A' },
  ]);
  const [deroule, setDeroule] = useState<DerouleCogo | null>(null);
  const [ajuste, setAjuste] = useState<AjustementCogo | null>(null);
  const [methodeCalcul, setMethodeCalcul] = useState<MethodeCalcul>('moindres_carres');
  const [methode, setMethode] = useState<MethodeLeve>('station_totale');
  const [precision, setPrecision] = useState('0.05');
  const [sourceActe, setSourceActe] = useState('');
  const [dossier, setDossier] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [depot, setDepot] = useState<string | null>(null);

  const tour = UNITES.find((u) => u.cle === unite)!.tour;

  const corps = useMemo(() => ({
    unite,
    depart: { nom: depart.nom.trim() || 'A', x: Number(depart.x), y: Number(depart.y), zone },
    lignes: lignes.filter((l) => l.de.trim() && l.vers.trim() && l.distance > 0),
  }), [unite, depart, zone, lignes]);

  const majLigne = (i: number, champ: keyof LigneCheminement, valeur: string) => {
    setLignes((L) => L.map((l, k) => k !== i ? l : {
      ...l,
      [champ]: champ === 'de' || champ === 'vers' ? valeur.toUpperCase() : Number(valeur),
    }));
    setDeroule(null); setAjuste(null); setDepot(null);
  };

  const jouer = useCallback(async (quoi: 'derouler' | 'ajuster') => {
    setOccupe(true); setErreur(null); setDepot(null);
    try {
      if (quoi === 'derouler') {
        setAjuste(null);
        setDeroule(await api.cogo.derouler(corps));
      } else {
        setDeroule(await api.cogo.derouler(corps));
        setAjuste(await api.cogo.ajuster({ ...corps, methodeCalcul }));
      }
    } catch (e) {
      setDeroule(null); setAjuste(null);
      setErreur(e instanceof ErreurApi ? e.message : 'Calcul impossible');
    } finally { setOccupe(false); }
  }, [corps, methodeCalcul]);

  const deposer = useCallback(async () => {
    setOccupe(true); setErreur(null);
    try {
      const r = await api.cogo.deposer({
        ...corps, methodeCalcul, methode,
        precisionAnnonceeM: Number(precision),
        sourceActe: sourceActe.trim() || undefined,
        dossierReference: dossier.trim() || undefined,
      });
      setDepot(`${r.releve.reference} déposé — ${r.lignesEnregistrees} ligne(s) conservées, ${r.releve.surfaceM2.toLocaleString('fr-FR')} m²`);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Dépôt impossible');
    } finally { setOccupe(false); }
  }, [corps, methodeCalcul, methode, precision, sourceActe, dossier]);

  /** Le verdict, en quatre états — jamais deux. */
  const verdict = useMemo(() => {
    if (!deroule) return null;
    if (!deroule.boucle) return { cle: 'ouvert', mot: 'Cheminement ouvert', dit: 'il ne revient pas au départ : il n’a pas de fermeture, et « pas de fermeture » ne se lit pas « fermeture parfaite »' };
    if (deroule.dansLaTolerance === false) return { cle: 'hors', mot: 'Hors tolérance', dit: `l’écart dépasse ${deroule.toleranceM} m — cherchez la faute, ne l’étalez pas` };
    if (ajuste && ajuste.degreLiberte === 0) return { cle: 'non-eprouve', mot: 'Non éprouvé', dit: 'autant d’observations que d’inconnues : les résidus sont nuls par construction, rien n’a pu être vérifié' };
    if (ajuste && ajuste.suspectes.length > 0) return { cle: 'suspect', mot: `${ajuste.suspectes.length} mesure(s) suspecte(s)`, dit: 'le test de Baarda les désigne — voir la table des résidus' };
    return { cle: 'conforme', mot: 'Conforme', dit: `l’écart est dans la tolérance de ${deroule.toleranceM} m` };
  }, [deroule, ajuste]);

  return (
    <div className="chem-ecran">
      <header className="chem-tete">
        <h2><Icone nom="cheminement" taille={20} /> Cheminement</h2>
        <p>
          Saisissez la parcelle <strong>telle que l’acte l’énonce</strong> — gisements et
          distances. Ces mesures sont conservées dans leur unité d’origine : le jour d’un
          litige, c’est la mesure du titre qui fait foi, pas le polygone qu’on en tire.
        </p>
      </header>

      <section className="chem-bloc">
        <h3>1 · Le repère</h3>
        <div className="chem-formulaire">
          <label>Unité des gisements
            <select value={unite} onChange={(e) => { setUnite(e.target.value as UniteAngle); setDeroule(null); setAjuste(null); }}>
              {UNITES.map((u) => <option key={u.cle} value={u.cle}>{u.libelle}</option>)}
            </select>
            <em>« 100 » vaut 90° en grades et 100° en degrés — les deux sont plausibles.</em>
          </label>
          <label>Fuseau
            <select value={zone} onChange={(e) => { setZone(Number(e.target.value)); setDeroule(null); setAjuste(null); }}>
              {FUSEAUX.map((f) => <option key={f.zone} value={f.zone}>{f.libelle}</option>)}
            </select>
          </label>
          <label>Point de départ
            <input value={depart.nom} onChange={(e) => setDepart({ ...depart, nom: e.target.value.toUpperCase() })} />
          </label>
          <label>X du départ (m)
            <input type="number" value={depart.x} onChange={(e) => setDepart({ ...depart, x: e.target.value })} />
          </label>
          <label>Y du départ (m)
            <input type="number" value={depart.y} onChange={(e) => setDepart({ ...depart, y: e.target.value })} />
          </label>
        </div>
        <p className="chem-aide">
          Le gisement se compte <strong>depuis le nord, dans le sens horaire</strong> : 0 mène au
          nord, {tour / 4} plein est. Ce n’est pas l’angle des mathématiques — les confondre
          rend une figure juste, tournée d’un quart de tour.
        </p>
      </section>

      <section className="chem-bloc">
        <h3>2 · Les lignes de l’acte</h3>
        <div className="chem-defile">
          <table className="chem-table">
            <thead>
              <tr><th>De</th><th>Vers</th><th>Gisement ({unite === 'grade' ? 'gon' : '°'})</th><th>Distance (m)</th><th /></tr>
            </thead>
            <tbody>
              {lignes.map((l, i) => (
                <tr key={i}>
                  <td><input className="chem-court" value={l.de} onChange={(e) => majLigne(i, 'de', e.target.value)} /></td>
                  <td><input className="chem-court" value={l.vers} onChange={(e) => majLigne(i, 'vers', e.target.value)} /></td>
                  <td><input type="number" step="0.0001" min="0" max={tour}
                             value={l.gisement || ''} onChange={(e) => majLigne(i, 'gisement', e.target.value)} /></td>
                  <td><input type="number" step="0.001" min="0"
                             value={l.distance || ''} onChange={(e) => majLigne(i, 'distance', e.target.value)} /></td>
                  <td><button className="chem-discret" onClick={() => { setLignes((L) => L.filter((_, k) => k !== i)); setDeroule(null); setAjuste(null); }}>retirer</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="chem-gestes">
          <button className="chem-discret" onClick={() => setLignes((L) => [...L, { ...LIGNE_VIDE }])}>+ une ligne</button>
          <button className="chem-principal" disabled={occupe || corps.lignes.length < 3} onClick={() => void jouer('derouler')}>
            {occupe ? 'Calcul…' : 'Dérouler — sans rien écrire'}
          </button>
          <label className="chem-inline">
            <select value={methodeCalcul} onChange={(e) => { setMethodeCalcul(e.target.value as MethodeCalcul); setAjuste(null); }}>
              <option value="moindres_carres">Moindres carrés</option>
              <option value="bowditch">Bowditch (règle de la boussole)</option>
            </select>
          </label>
          <button className="chem-principal" disabled={occupe || corps.lignes.length < 3} onClick={() => void jouer('ajuster')}>
            Compenser
          </button>
        </div>
      </section>

      {erreur && <div className="chem-refus"><Icone nom="alerte" taille={17} /> <b>{erreur}</b></div>}

      {deroule && verdict && (
        <section className="chem-bloc">
          <h3>3 · Ce que l’acte dessine</h3>
          <div className={`chem-verdict chem-${verdict.cle}`}>
            <b>{verdict.mot}</b><span>{verdict.dit}</span>
          </div>
          <div className="chem-chiffres">
            <div><b>{deroule.surfaceM2.toLocaleString('fr-FR')}</b><span>m² (plan)</span></div>
            <div><b>{deroule.perimetreM.toLocaleString('fr-FR')}</b><span>m de périmètre</span></div>
            <div><b>{deroule.fermeture ? `${deroule.fermeture.ecartM} m` : '—'}</b><span>écart de fermeture</span></div>
            <div><b>{deroule.fermeture?.precisionRelative ? `1/${deroule.fermeture.precisionRelative.toLocaleString('fr-FR')}` : '—'}</b><span>précision relative</span></div>
            <div><b>{deroule.toleranceM} m</b><span>tolérance</span></div>
          </div>

          {ajuste && (
            <>
              {ajuste.avertissement && (
                <p className="chem-avert"><Icone nom="alerte" taille={15} /> {ajuste.avertissement}</p>
              )}
              <div className="chem-chiffres">
                <div><b>{ajuste.degreLiberte ?? '—'}</b><span>degré de liberté</span></div>
                <div><b>{ajuste.sigmaZero ?? '—'}</b><span>σ₀</span></div>
                <div><b>{ajuste.testGlobalPasse === null ? 'non éprouvé' : ajuste.testGlobalPasse ? 'passe' : 'échoue'}</b><span>test global</span></div>
                <div><b>{ajuste.surfaceApresM2.toLocaleString('fr-FR')}</b><span>m² après compensation</span></div>
              </div>

              <h4 className="chem-sous">Les sommets, et ce qu’on sait de leur position</h4>
              <div className="chem-defile">
                <table className="chem-table">
                  <thead><tr><th>Point</th><th>Longitude</th><th>Latitude</th><th>Incertitude</th></tr></thead>
                  <tbody>
                    {ajuste.points.map((p) => (
                      <tr key={p.nom}>
                        <td>{p.nom}{p.fixe && <em> (tenu)</em>}</td>
                        <td>{p.longitude.toFixed(7)}</td>
                        <td>{p.latitude.toFixed(7)}</td>
                        <td>{p.fixe ? <em>point tenu — aucune incertitude</em>
                              : p.ellipse ? `${p.ellipse.demiGrandAxeM} × ${p.ellipse.demiPetitAxeM} m` : <em>—</em>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {ajuste.suspectes.length > 0 && (
                <>
                  <h4 className="chem-sous">Ce que le test de Baarda désigne</h4>
                  <p className="chem-aide">
                    Bowditch aurait étalé cette faute sur tous les côtés et fermé à zéro.
                    Les moindres carrés la <strong>montrent</strong> : la mesure au résidu
                    normalisé le plus fort est la première à vérifier sur le terrain.
                  </p>
                  <div className="chem-defile">
                    <table className="chem-table">
                      <thead><tr><th>Observation</th><th>Ligne</th><th>Résidu</th><th>Résidu normalisé</th></tr></thead>
                      <tbody>
                        {[...ajuste.suspectes].sort((a, b) => b.residuNormalise - a.residuNormalise).map((r, i) => (
                          <tr key={i} className="chem-ligne-suspecte">
                            <td>{r.type === 'distance' ? 'Distance' : 'Gisement'}</td>
                            <td>{r.de} → {r.vers}</td>
                            <td>{r.residu.toFixed(4)}</td>
                            <td>{r.residuNormalise.toFixed(1)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </section>
      )}

      {deroule && (
        <section className="chem-bloc">
          <h3>4 · Déposer comme levé</h3>
          <p className="chem-aide">
            Le dépôt conserve <strong>les deux</strong> : les mesures de l’acte, dans leur
            unité d’origine, et la géométrie qu’elles produisent. Le levé n’est opposable
            à personne tant qu’un tiers ne l’a pas validé.
          </p>
          <div className="chem-formulaire">
            <label>Source (l’acte qui énonce ces mesures)
              <input value={sourceActe} onChange={(e) => setSourceActe(e.target.value)}
                     placeholder="TF 1247 Kaloum, acte du 12 mars 1974" />
            </label>
            <label>Méthode
              <select value={methode} onChange={(e) => setMethode(e.target.value as MethodeLeve)}>
                {METHODES.map((m) => <option key={m.cle} value={m.cle}>{m.libelle}</option>)}
              </select>
            </label>
            <label>Précision annoncée (m)
              <input type="number" step="0.01" min="0.01" value={precision} onChange={(e) => setPrecision(e.target.value)} />
            </label>
            <label>Dossier (facultatif)
              <input value={dossier} onChange={(e) => setDossier(e.target.value)} placeholder="GN-DOS-000000004" />
            </label>
          </div>
          <div className="chem-gestes">
            <button className="chem-principal" disabled={occupe} onClick={() => void deposer()}>
              {occupe ? 'Dépôt…' : 'Déposer le levé'}
            </button>
          </div>
          {depot && <div className="chem-fait"><Icone nom="valide" taille={17} /> {depot}</div>}
        </section>
      )}
    </div>
  );
}
